import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import {
  FetchPDFTemplatesAction,
  SetActivePDFTemplateAction,
  FetchPDFTemplateInstancesAction,
  DeletePDFTemplateAction,
} from "../../../Actions/Forms";
import {
  FileText,
  Plus,
  ChevronRight,
  ChevronDown,
  Layers,
  Clock,
  Download,
  Eye,
  Loader2,
  Trash2,
  Share2,
  Copy,
  Check,
  X,
} from "lucide-react";
import axios from "../../../Actions/Axios";
import PDFTemplateUpload from "./PDFTemplateUpload";

class SmartFlowHome extends Component {
  static propTypes = {
    pdfTemplates: PropTypes.array,
    FetchPDFTemplatesAction: PropTypes.func.isRequired,
    SetActivePDFTemplateAction: PropTypes.func.isRequired,
    FetchPDFTemplateInstancesAction: PropTypes.func.isRequired,
    DeletePDFTemplateAction: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
  };

  state = {
    expandedTemplateId: null,
    instances: {},
    loadingInstances: {},
    activeTab: {},
    deletingTemplateId: null,
    deleteError: null,
    showUploadModal: false,
    // Share modal
    shareTemplateId: null,
    shareToken: null,
    shareLoading: false,
    shareCopied: false,
  };

  componentDidMount() {
    this.props.FetchPDFTemplatesAction();
  }

  handleSelectTemplate = (template) => {
    this.props.SetActivePDFTemplateAction(template);
    this.props.navigate(`/dashboard/smartflow/${template.id}/fill`);
  };

  handleEditTemplate = (e, template) => {
    e.stopPropagation();
    this.props.SetActivePDFTemplateAction(template);
    this.props.navigate(`/dashboard/smartflow/${template.id}/map`);
  };

  handleToggleHistory = async (e, template) => {
    e.stopPropagation();
    const { expandedTemplateId } = this.state;

    if (expandedTemplateId === template.id) {
      this.setState({ expandedTemplateId: null });
      return;
    }

    this.setState({ expandedTemplateId: template.id });

    if (!this.state.instances[template.id]) {
      this.setState((prev) => ({
        loadingInstances: { ...prev.loadingInstances, [template.id]: true },
      }));
      try {
        const data = await this.props.FetchPDFTemplateInstancesAction(template.id);
        this.setState((prev) => ({
          instances: { ...prev.instances, [template.id]: data },
          loadingInstances: { ...prev.loadingInstances, [template.id]: false },
        }));
      } catch {
        this.setState((prev) => ({
          instances: { ...prev.instances, [template.id]: [] },
          loadingInstances: { ...prev.loadingInstances, [template.id]: false },
        }));
      }
    }
  };

  handleDeleteTemplate = async (e, template) => {
    e.stopPropagation();
    const confirmDelete = window.confirm(
      `Delete "${template.name}"? This will also remove its filled submissions.`
    );
    if (!confirmDelete) return;

    this.setState({ deletingTemplateId: template.id, deleteError: null });
    try {
      await this.props.DeletePDFTemplateAction(template.id);
      this.setState((prev) => {
        const nextInstances = { ...prev.instances };
        const nextLoadingInstances = { ...prev.loadingInstances };
        delete nextInstances[template.id];
        delete nextLoadingInstances[template.id];
        return {
          deletingTemplateId: null,
          expandedTemplateId:
            prev.expandedTemplateId === template.id ? null : prev.expandedTemplateId,
          instances: nextInstances,
          loadingInstances: nextLoadingInstances,
        };
      });
    } catch {
      this.setState({
        deletingTemplateId: null,
        deleteError: "Failed to delete template. Please try again.",
      });
    }
  };

  handleShare = async (e, template) => {
    e.stopPropagation();
    this.setState({ shareTemplateId: template.id, shareToken: template.share_token || null, shareLoading: !template.share_token });
    if (!template.share_token) {
      try {
        const res = await axios.post(`/api/form/pdf-templates/${template.id}/share/`);
        this.setState({ shareToken: res.data.share_token, shareLoading: false });
        // Update the template in redux store
        this.props.FetchPDFTemplatesAction();
      } catch {
        this.setState({ shareLoading: false });
      }
    }
  };

  handleRevokeShare = async () => {
    const { shareTemplateId } = this.state;
    if (!shareTemplateId) return;
    try {
      await axios.delete(`/api/form/pdf-templates/${shareTemplateId}/share/`);
      this.setState({ shareToken: null });
      this.props.FetchPDFTemplatesAction();
    } catch {}
  };

  handleCopyLink = () => {
    const { shareToken } = this.state;
    if (!shareToken) return;
    const link = `${window.location.origin}/#/forms/fill/${shareToken}`;
    navigator.clipboard.writeText(link).then(() => {
      this.setState({ shareCopied: true });
      setTimeout(() => this.setState({ shareCopied: false }), 2000);
    });
  };

  formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  renderInstances = (template) => {
    const { instances, loadingInstances } = this.state;
    const loading = loadingInstances[template.id];
    const list = instances[template.id] || [];

    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
        </div>
      );
    }

    if (list.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <p className="font-inter text-sm text-gray-400">No filled forms yet.</p>
          <button
            onClick={() => this.handleSelectTemplate(template)}
            className="flex items-center gap-1.5 text-xs font-inter font-medium text-blue-600 hover:underline"
          >
            <Plus className="w-3.5 h-3.5" /> Fill this template
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col divide-y divide-gray-50">
        {list.map((instance) => (
          <div key={instance.id} className="flex items-center gap-3 px-5 py-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-inter font-medium text-sm text-gray-800">
                Submission #{instance.id}
              </p>
              <p className="font-inter text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3" />
                {this.formatDate(instance.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() =>
                  this.props.navigate(
                    `/dashboard/smartflow/instance/${instance.id}/preview`
                  )
                }
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-inter font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                View
              </button>
              {instance.rendered_pdf && (
                <a
                  href={instance.rendered_pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-inter font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  render() {
    const { pdfTemplates } = this.props;
    const { expandedTemplateId, deletingTemplateId, deleteError, showUploadModal,
            shareTemplateId, shareToken, shareLoading, shareCopied } = this.state;

    return (
      <>
      <div
        className="relative flex w-full border-l"
        style={{
          background:
            "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
        }}
      >
        <div className="w-full flex flex-col px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="font-SFPro font-bold text-2xl text-gray-900">SmartFlow</h1>
            <button
              onClick={() => this.setState({ showUploadModal: true })}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              New Template
            </button>
          </div>

          {/* Template List */}
          {deleteError && (
            <div className="px-4 py-2.5 border border-red-200 bg-red-50 text-red-600 font-inter text-sm rounded-xl">
              {deleteError}
            </div>
          )}
          {!pdfTemplates ? (
            <div className="flex items-center justify-center h-48 text-gray-400 font-inter text-sm">
              Loading templates…
            </div>
          ) : pdfTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
                <Layers className="w-8 h-8 text-blue-400" />
              </div>
              <p className="font-inter text-gray-400 text-sm text-center max-w-xs">
                No PDF templates yet. Upload your first PDF to get started.
              </p>
              <button
                onClick={() => this.setState({ showUploadModal: true })}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Upload PDF Template
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {pdfTemplates.map((template) => {
                const isExpanded = expandedTemplateId === template.id;
                return (
                  <div
                    key={template.id}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-blue-200 transition-colors"
                  >
                    {/* Template row */}
                    <div
                      onClick={() => this.handleSelectTemplate(template)}
                      className="flex items-center gap-4 px-5 py-4 cursor-pointer group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-inter font-semibold text-gray-900 text-sm truncate">
                          {template.name}
                        </p>
                        <p className="font-inter text-xs text-gray-400 mt-0.5">
                          {template.fields?.length || 0} field{template.fields?.length !== 1 ? "s" : ""} mapped
                          {template.form_type_name ? ` · ${template.form_type_name}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={(e) => this.handleEditTemplate(e, template)}
                          className="px-3 py-1.5 text-xs font-inter font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          Edit fields
                        </button>
                        <button
                          onClick={(e) => this.handleShare(e, template)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-inter font-medium text-blue-500 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Share
                        </button>
                        <button
                          onClick={(e) => this.handleDeleteTemplate(e, template)}
                          disabled={deletingTemplateId === template.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-inter font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {deletingTemplateId === template.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                          Delete
                        </button>
                        <button
                          onClick={(e) => this.handleToggleHistory(e, template)}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-inter font-medium border rounded-lg transition-colors ${
                            isExpanded
                              ? "bg-blue-50 text-blue-600 border-blue-200"
                              : "text-gray-500 border-gray-200 hover:bg-gray-50 opacity-0 group-hover:opacity-100"
                          }`}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          Filled forms
                          <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          />
                        </button>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                      </div>
                    </div>

                    {/* Filled forms drawer */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50/50">
                        <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100">
                          <p className="font-inter font-semibold text-xs text-gray-500 uppercase tracking-wide">
                            Filled Submissions
                          </p>
                          <button
                            onClick={() => this.handleSelectTemplate(template)}
                            className="flex items-center gap-1.5 text-xs font-inter font-medium text-blue-600 hover:underline"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            New submission
                          </button>
                        </div>
                        {this.renderInstances(template)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <PDFTemplateUpload
        isOpen={showUploadModal}
        onClose={() => this.setState({ showUploadModal: false })}
      />

      {/* Share modal */}
      {shareTemplateId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-SFPro font-bold text-lg text-gray-900">Share form</h3>
              <button
                onClick={() => this.setState({ shareTemplateId: null, shareToken: null, shareCopied: false })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {shareLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
              </div>
            ) : shareToken ? (
              <>
                <p className="font-inter text-sm text-gray-500 mb-3">
                  Anyone with this link can fill out the form without logging in.
                </p>
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 mb-4">
                  <span className="flex-1 font-mono text-xs text-gray-600 truncate">
                    {`${window.location.origin}/#/forms/fill/${shareToken}`}
                  </span>
                  <button
                    onClick={this.handleCopyLink}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white font-inter font-medium text-xs rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {shareCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {shareCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <button
                  onClick={this.handleRevokeShare}
                  className="text-xs font-inter text-red-500 hover:underline"
                >
                  Revoke link
                </button>
              </>
            ) : (
              <p className="font-inter text-sm text-gray-400 text-center py-4">
                Failed to generate share link.
              </p>
            )}
          </div>
        </div>
      )}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  pdfTemplates: state.forms.pdfTemplates,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    FetchPDFTemplatesAction,
    SetActivePDFTemplateAction,
    FetchPDFTemplateInstancesAction,
    DeletePDFTemplateAction,
  })(SmartFlowHome)
);
